import React, { useState, useEffect, useMemo } from 'react';
import { Button, Form, Card } from 'react-bootstrap';
import { Plus, Edit2, Trash2, GripVertical } from 'lucide-react';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ProjectModel, ChecklistItem } from '../../../types/projects';
import { ProjectChecklistModal } from '../modals/ProjectChecklistModal';
import { ConfirmDeleteModal } from '../../../Components/ConfirmDeleteModal';
import { MetadataInfoButton } from '../../../Components/MetadataInfoButton';
import { useUserNames } from '../../../Helpers/useUserNames';

interface ProjectChecklistsSectionProps {
    project: ProjectModel;
    onAddChecklist: (title: string) => Promise<void>;
    onUpdateChecklistTitle: (checklistKey: string, title: string) => Promise<void>;
    onDeleteChecklist: (checklistKey: string) => Promise<void>;
    onAddChecklistItem: (checklistKey: string, text: string) => Promise<void>;
    onUpdateChecklistItemText: (checklistKey: string, itemKey: string, text: string) => Promise<void>;
    onToggleChecklistItem: (checklistKey: string, itemKey: string, completed: boolean) => Promise<void>;
    onDeleteChecklistItem: (checklistKey: string, itemKey: string) => Promise<void>;
    onReorderChecklistItems: (checklistKey: string, orderedItemKeys: string[]) => Promise<void>;
    submitting?: boolean;
    /**
     * When true, renders a read-only view: hides the add-checklist button, the
     * add-item input, the per-item and per-checklist delete buttons and the
     * edit-title control, and disables drag-and-drop reordering. Checklist
     * titles, items, progress and each item's completed checkbox stay visible
     * (the checkbox is disabled so it shows state but cannot be toggled).
     * Defaults to false so the Projects pages behave exactly as before.
     */
    readOnly?: boolean;
}

// Sortable item component for the preview
interface SortableChecklistItemProps {
    item: ChecklistItem;
    checklistKey: string;
    onToggle: (checklistKey: string, itemKey: string, completed: boolean) => Promise<void>;
    submitting: boolean;
    completedByName?: string;
    readOnly?: boolean;
}

const SortableChecklistItem: React.FC<SortableChecklistItemProps> = ({
    item,
    checklistKey,
    onToggle,
    submitting,
    completedByName,
    readOnly = false,
}) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: item.key, disabled: readOnly });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <div
            ref={setNodeRef}
            style={{
                ...style,
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '8px',
                backgroundColor: item.completed ? '#f0f8ff' : 'transparent',
                borderRadius: '4px',
            }}
        >
            {!readOnly && (
                <div
                    {...attributes}
                    {...listeners}
                    style={{
                        cursor: 'grab',
                        display: 'flex',
                        alignItems: 'center',
                        padding: '4px',
                        color: '#999',
                    }}
                >
                    <GripVertical size={16} />
                </div>
            )}
            <Form.Check
                type="checkbox"
                checked={item.completed}
                onChange={(e) => onToggle(checklistKey, item.key, e.target.checked)}
                disabled={readOnly || submitting}
            />
            <span style={{
                flex: 1,
                textDecoration: item.completed ? 'line-through' : 'none',
                color: item.completed ? '#666' : '#333',
                fontSize: '14px'
            }}>
                {item.text}
            </span>
            {item.completed && item.completedBy && (
                <MetadataInfoButton
                    entries={[
                        {
                            label: 'Slutförd',
                            date: item.completedAt,
                            userName: completedByName || 'Okänd användare',
                        },
                    ]}
                />
            )}
        </div>
    );
};

export const ProjectChecklistsSection: React.FC<ProjectChecklistsSectionProps> = ({
    project,
    onAddChecklist,
    onUpdateChecklistTitle,
    onDeleteChecklist,
    onAddChecklistItem,
    onUpdateChecklistItemText,
    onToggleChecklistItem,
    onDeleteChecklistItem,
    onReorderChecklistItems,
    submitting = false,
    readOnly = false,
}) => {
    const [showModal, setShowModal] = useState(false);
    const [editingChecklistKey, setEditingChecklistKey] = useState<string | null>(null);
    const [isNewChecklist, setIsNewChecklist] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [pendingDeleteChecklistKey, setPendingDeleteChecklistKey] = useState<string | null>(null);

    const { userNames, loadUserNames } = useUserNames();

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const checklists = useMemo(
        () => project.checklists ? Object.entries(project.checklists) : [],
        [project.checklists]
    );

    // Extract UIDs for user name loading
    const checklistUids = useMemo(() => {
        const uids: string[] = [];
        checklists.forEach(([, checklist]) => {
            if (checklist.createdBy) uids.push(checklist.createdBy);
            if (checklist.items) {
                Object.values(checklist.items).forEach((item) => {
                    if (item.completedBy) uids.push(item.completedBy);
                });
            }
        });
        return uids;
    }, [checklists]);

    // Load user names for all checklists
    useEffect(() => {
        if (checklistUids.length > 0) {
            loadUserNames(checklistUids);
        }
    }, [checklistUids, loadUserNames]);

    // Get the live checklist data from the project (updates in real-time)
    const editingChecklist = editingChecklistKey && project.checklists
        ? project.checklists[editingChecklistKey] || null
        : null;

    // Helper to sort items by sortOrder
    const getSortedItems = (items: { [key: string]: ChecklistItem } | undefined): [string, ChecklistItem][] => {
        if (!items) return [];
        return Object.entries(items).sort((a, b) => {
            const orderA = a[1].sortOrder ?? 0;
            const orderB = b[1].sortOrder ?? 0;
            return orderA - orderB;
        });
    };

    // Handle drag end for reordering - returns a handler bound to the checklist
    const createDragEndHandler = (checklistKey: string, items: [string, ChecklistItem][]) => {
        return (event: DragEndEvent) => {
            const { active, over } = event;
            if (!over || active.id === over.id) return;

            const oldIndex = items.findIndex(([key]) => key === active.id);
            const newIndex = items.findIndex(([key]) => key === over.id);

            if (oldIndex !== -1 && newIndex !== -1) {
                const reorderedItems = arrayMove(items, oldIndex, newIndex);
                const orderedKeys = reorderedItems.map(([key]) => key);
                onReorderChecklistItems(checklistKey, orderedKeys);
            }
        };
    };

    const handleOpenAddModal = () => {
        setEditingChecklistKey(null);
        setIsNewChecklist(true);
        setShowModal(true);
    };

    const handleOpenEditModal = (checklistKey: string) => {
        setEditingChecklistKey(checklistKey);
        setIsNewChecklist(false);
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditingChecklistKey(null);
        setIsNewChecklist(false);
    };

    const handleSaveTitle = async (title: string) => {
        if (isNewChecklist) {
            await onAddChecklist(title);
        } else if (editingChecklistKey) {
            await onUpdateChecklistTitle(editingChecklistKey, title);
        }
    };

    const handleAddItem = async (text: string) => {
        if (editingChecklistKey) {
            await onAddChecklistItem(editingChecklistKey, text);
        }
    };

    const handleUpdateItem = async (itemKey: string, text: string) => {
        if (editingChecklistKey) {
            await onUpdateChecklistItemText(editingChecklistKey, itemKey, text);
        }
    };

    const handleDeleteItem = async (itemKey: string) => {
        if (editingChecklistKey) {
            await onDeleteChecklistItem(editingChecklistKey, itemKey);
        }
    };

    const handleReorderItems = async (orderedItemKeys: string[]) => {
        if (editingChecklistKey) {
            await onReorderChecklistItems(editingChecklistKey, orderedItemKeys);
        }
    };

    const handleDeleteChecklistClick = (checklistKey: string) => {
        setPendingDeleteChecklistKey(checklistKey);
        setShowDeleteModal(true);
    };

    const handleConfirmDeleteChecklist = async () => {
        if (pendingDeleteChecklistKey) {
            await onDeleteChecklist(pendingDeleteChecklistKey);
            setPendingDeleteChecklistKey(null);
        }
    };

    return (
        <div style={{
            marginTop: '24px',
            paddingTop: '24px',
            borderTop: '1px solid #dee2e6'
        }}>
            <div style={{ border: "1px solid #dee2e6", borderRadius: "10px", backgroundColor: "#fff" }}>
                <div style={{
                    backgroundColor: '#f8f9fa',
                    padding: '12px 16px',
                    borderBottom: '1px solid #dee2e6',
                    borderTopLeftRadius: '10px',
                    borderTopRightRadius: '10px'
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <h5 style={{ margin: 0 }}>Checklistor</h5>
                        {!readOnly && (
                            <Button
                                variant="primary"
                                size="sm"
                                onClick={handleOpenAddModal}
                                disabled={submitting}
                            >
                                <Plus size={14} className="me-1" />
                                Lägg till checklista
                            </Button>
                        )}
                    </div>
                </div>
                <div style={{ padding: '24px' }}>
                    {checklists.length > 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                            {checklists.map(([checklistKey, checklist]) => {
                                const sortedItems = getSortedItems(checklist.items);

                                return (
                                    <Card key={checklistKey} className="checklist-card" style={{ border: '1px solid #dee2e6' }}>
                                        <Card.Header style={{ backgroundColor: '#f8f9fa', padding: '12px 16px' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    <h6 style={{ margin: 0, fontSize: '16px' }}>{checklist.title}</h6>
                                                    {checklist.createdBy && (
                                                        <MetadataInfoButton
                                                            entries={[
                                                                {
                                                                    label: 'Skapad',
                                                                    date: checklist.createdAt,
                                                                    userName: userNames[checklist.createdBy] || 'Okänd användare',
                                                                },
                                                            ]}
                                                        />
                                                    )}
                                                </div>
                                                {!readOnly && (
                                                    <div style={{ display: 'flex', gap: '5px' }}>
                                                        <Button
                                                            variant="outline-primary"
                                                            size="sm"
                                                            onClick={() => handleOpenEditModal(checklistKey)}
                                                            disabled={submitting}
                                                        >
                                                            <Edit2 size={14} />
                                                        </Button>
                                                        <Button
                                                            variant="outline-danger"
                                                            size="sm"
                                                            onClick={() => handleDeleteChecklistClick(checklistKey)}
                                                            disabled={submitting}
                                                        >
                                                            <Trash2 size={14} />
                                                        </Button>
                                                    </div>
                                                )}
                                            </div>
                                        </Card.Header>
                                        <Card.Body style={{ padding: '16px' }}>
                                            {sortedItems.length > 0 ? (
                                                <DndContext
                                                    sensors={readOnly ? [] : sensors}
                                                    collisionDetection={closestCenter}
                                                    onDragEnd={readOnly ? undefined : createDragEndHandler(checklistKey, sortedItems)}
                                                >
                                                    <SortableContext
                                                        items={sortedItems.map(([key]) => key)}
                                                        strategy={verticalListSortingStrategy}
                                                    >
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                            {sortedItems.map(([itemKey, item]) => (
                                                                <SortableChecklistItem
                                                                    key={itemKey}
                                                                    item={{ ...item, key: itemKey }}
                                                                    checklistKey={checklistKey}
                                                                    onToggle={onToggleChecklistItem}
                                                                    submitting={submitting}
                                                                    completedByName={item.completedBy ? userNames[item.completedBy] || undefined : undefined}
                                                                    readOnly={readOnly}
                                                                />
                                                            ))}
                                                        </div>
                                                    </SortableContext>
                                                </DndContext>
                                            ) : (
                                                <p style={{ color: '#666', fontStyle: 'italic', margin: 0, fontSize: '14px' }}>
                                                    Inga punkter ännu
                                                </p>
                                            )}
                                        </Card.Body>
                                    </Card>
                                );
                            })}
                        </div>
                    ) : (
                        <p style={{ color: '#666', fontStyle: 'italic' }}>Inga checklistor ännu</p>
                    )}
                </div>
            </div>

            {!readOnly && (
                <>
                    <ProjectChecklistModal
                        show={showModal}
                        onHide={handleCloseModal}
                        onSaveTitle={handleSaveTitle}
                        onAddItem={handleAddItem}
                        onUpdateItem={handleUpdateItem}
                        onDeleteItem={handleDeleteItem}
                        onReorderItems={handleReorderItems}
                        existingChecklist={editingChecklist}
                        isNewChecklist={isNewChecklist}
                        submitting={submitting}
                    />

                    <ConfirmDeleteModal
                        show={showDeleteModal}
                        onHide={() => {
                            setShowDeleteModal(false);
                            setPendingDeleteChecklistKey(null);
                        }}
                        onConfirm={handleConfirmDeleteChecklist}
                        title="Radera checklista"
                        message="Är du säker på att du vill radera denna checklista? Alla punkter i checklistan kommer också att raderas. Detta kan inte ångras."
                    />
                </>
            )}
        </div>
    );
};
